import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { Loading } from "../../../../components/Loading";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { getReportLevel, buildReportQueryString, reportSubLevelName } from "../adminReportsUtil";
import { ReportLevel, AdminReportsResponse } from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import {
	UsageAndPerformanceByPromptRow,
	useLazyPromptUsageAndPerformanceQuery,
} from "./adminPromptReportApiSlice";
import { UsageAndPerformanceByPromptTable } from "./UsageAndPerformanceByPromptTable";
import UsageAndPerformanceByPromptChart from "./UsageAndPerformanceByPromptChart";

export default function UsageAndPerformanceByPrompt() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [data, setData] = useState<AdminReportsResponse<UsageAndPerformanceByPromptRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [hideRows, setHideRows] = useState(false);
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [callPromptReport] = useLazyPromptUsageAndPerformanceQuery();

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		callPromptReport(
			{
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: schoolFilter === 0 ? undefined : schoolFilter,
			},
			true //cache the results
		)
			.unwrap()
			.then((payload) => {
				setData(payload.response);
				setLastUpdated(payload.lastUpdateDate);
				setReportLevel(getReportLevel(districtFilter, schoolFilter));
			})
			.catch((error) => {
				console.log(error.data);
				navigate("/adminReports");
			});
	}, [districtFilter, schoolFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
		});
	}, [districtFilter, schoolFilter]);

	const instructionByLevel = useMemo(() => {
		if (reportLevel === ReportLevel.State) {
			return "pre-packaged";
		} else if (reportLevel === ReportLevel.District) {
			return "pre-packaged and district";
		} else if (reportLevel === ReportLevel.School) {
			return "pre-packaged, district and school";
		}
	}, [reportLevel]);

	return (
		<section>
			<h1>{reportLevel} Usage and Performance by Prompt</h1>
			<p className="mt-4 print:hidden">{`The Usage and Performance by Prompt Report shows the number of essay and draft submissions, average number of drafts per essay and the average trait and total scores for each prompt title. All ${instructionByLevel} prompts that have been written to are included in the report. ${schoolOptions.length > 1 || districtOptions.length > 1 ? `Use the filters below to view data for that selection.` : ""} Select a prompt name in the table to view what ${reportSubLevelName(reportLevel).toLowerCase()}s have written to the prompt. Click the three dots to print or download.`}</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end">
							<DistrictSchoolFilters
								districtOptions={districtOptions}
								schoolOptions={schoolOptions}
								districtFilter={districtFilter}
								schoolFilter={schoolFilter}
								setDistrictFilter={setDistrictFilter}
								setSchoolFilter={setSchoolFilter}
							/>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={data.rows.length !== 0}
								hideRows={hideRows}
								setHideRows={setHideRows}
								uniqueKey="prompt-report"
								downloadExcelLink={`${aspUrl}/AdminReports/PromptReportExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/PromptReportCsv?${queryString}`}
							/>
						</div>

						<UsageAndPerformanceByPromptTable
							className="mt-4"
							level={reportLevel}
							data={data}
							hideRows={hideRows}
						/>
						<UsageAndPerformanceByPromptChart
							level={reportLevel}
							data={data.rows}
							hasData={data.rows.length > 0}
						/>
					</>
				)}
			</div>
		</section>
	);
}
